// apps/web/src/adapters/WebStorageAdapter.ts
// ─────────────────────────────────────────────────────────────────────────
// IndexedDB-backed key/value storage. Implements StorageAdapter from core.
// ─────────────────────────────────────────────────────────────────────────

import type { StorageAdapter } from '@nisk/core';
import { db } from './DexieDB';

type AnyTable = ReturnType<typeof db.table>;

function getTable(name: string): AnyTable {
	// Cast to any to access tables by string name; Dexie supports this
	return (db as unknown as Record<string, AnyTable>)[name] ?? db.table(name);
}

export class WebStorageAdapter implements StorageAdapter {
	async get<T = unknown>(table: string, key: string): Promise<T | null> {
		const row = await getTable(table).get(key);
		return row ? ((row as { value: T }).value ?? null) : null;
	}

	async set<T = unknown>(
		table: string,
		key: string,
		value: T,
	): Promise<void> {
		await getTable(table).put({ key, value });
	}

	async delete(table: string, key: string): Promise<void> {
		await getTable(table).delete(key);
	}

	async listKeys(table: string, prefix?: string): Promise<string[]> {
		const rows = await getTable(table).toArray();
		const keys = (rows as Array<{ key: string }>).map((r) => r.key);
		return prefix ? keys.filter((k) => k.startsWith(prefix)) : keys;
	}

	async listValues<T = unknown>(
		table: string,
		prefix?: string,
	): Promise<T[]> {
		const rows = await getTable(table).toArray();
		const typedRows = rows as Array<{ key: string; value: T }>;
		const filtered = prefix
			? typedRows.filter((r) => r.key.startsWith(prefix))
			: typedRows;
		return filtered.map((r) => r.value);
	}

	async count(table: string): Promise<number> {
		return getTable(table).count();
	}

	async clear(table: string): Promise<void> {
		await getTable(table).clear();
	}

	async transaction<T>(tables: string[], fn: () => Promise<T>): Promise<T> {
		return db.transaction(
			'rw',
			tables.map((t) => getTable(t)),
			fn,
		);
	}
}

// Singleton — apps share one adapter instance
export const storage = new WebStorageAdapter();
